import type { MetaItem } from '@/Utils/Meta/types';
import type { RouteLocationRaw } from 'vue-router';
import { slugify } from '@/Utils/Core/string';
import { buildWcRouteHref, type WcRouterMode } from '@/Utils/Wc/routerConfig';

export type DetailLinkMode = 'default' | 'canonical';

export function normalizeDetailLinkMode(value: unknown): DetailLinkMode {
  return typeof value === 'string' && value.trim().toLowerCase() === 'canonical'
    ? 'canonical'
    : 'default';
}

export type ResolvedDetailLink =
  { kind: 'href'; href: string } | { kind: 'route'; to: RouteLocationRaw };

interface ResolveDetailLinkOptions {
  mode?: DetailLinkMode;
  explicitUrl?: string;
  routerMode?: WcRouterMode;
  routerBasePath?: string;
}

export function buildDefaultDetailRoute(item: MetaItem): RouteLocationRaw {
  return {
    name: 'Detail',
    params: {
      type: item.type,
      id: item.global_id,
      title: slugify(item.title),
    },
  };
}

function findCanonicalUrl(item: MetaItem): string | undefined {
  const canonicalCandidates = item.media_objects?.filter(
    (mediaObject) =>
      typeof mediaObject?.rel === 'string' && mediaObject.rel.trim().toLowerCase() === 'canonical',
  );

  for (const candidate of canonicalCandidates ?? []) {
    if (typeof candidate.url !== 'string') continue;

    const value = candidate.url.trim();
    if (!value) continue;

    try {
      const url = new URL(value);
      if (url.protocol === 'http:' || url.protocol === 'https:') return value;
    } catch {
      // Invalid canonical URLs are ignored in favor of the redirect fallback.
    }
  }

  return undefined;
}

export function buildCanonicalDetailHref(
  item: MetaItem,
  {
    routerMode = 'memory',
    routerBasePath = '/',
  }: { routerMode?: WcRouterMode; routerBasePath?: string } = {},
): string | undefined {
  const canonicalUrl = findCanonicalUrl(item);
  if (canonicalUrl) return canonicalUrl;

  const globalId = String(item.global_id ?? '').trim();
  const dataType = String(item.type ?? '').trim();

  if (!globalId || !dataType) return undefined;

  const fallbackPath = `/pages-detail-redirect?global_id=${encodeURIComponent(globalId)}&dataType=${encodeURIComponent(dataType)}`;
  return buildWcRouteHref({
    path: fallbackPath,
    mode: routerMode,
    basePath: routerBasePath,
  });
}

export function resolveDetailLink(
  item: MetaItem,
  {
    mode = 'default',
    explicitUrl,
    routerMode = 'memory',
    routerBasePath = '/',
  }: ResolveDetailLinkOptions = {},
): ResolvedDetailLink {
  if (mode === 'canonical') {
    const href = buildCanonicalDetailHref(item, { routerMode, routerBasePath });
    if (href) return { kind: 'href', href };

    console.warn(
      '[DetailLink] Canonical mode requires a canonical URL or both global_id and type. Falling back to the existing detail link.',
      item,
    );
  }

  const normalizedExplicitUrl = explicitUrl?.trim();
  if (normalizedExplicitUrl) return { kind: 'href', href: normalizedExplicitUrl };

  return { kind: 'route', to: buildDefaultDetailRoute(item) };
}
